import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Search,
  Refresh,
  RestoreFromTrash,
} from '@mui/icons-material';
import apiService from '../services/api';
import { Member } from '../types';

const DeletedMembers: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{show: boolean, message: string, type: 'success' | 'error' | 'info'}>({
    show: false,
    message: '',
    type: 'info'
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Search debounce
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const shouldRefocusSearchRef = useRef(false);

  // Track component mount state to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // AbortController for API requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const fetchDeletedMembers = useCallback(async () => {
    if (!isMountedRef.current) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (!abortControllerRef.current) {
        abortControllerRef.current = new AbortController();
      }

      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      const response = await apiService.getDeletedMembers(params, abortControllerRef.current.signal);

      if (isMountedRef.current) {
        setMembers(response.data || []);
        setTotalCount(response.pagination?.total || 0);
        setLoading(false);
      }
    } catch (error: any) {
      if (isMountedRef.current) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
          return;
        }

        setError(error.response?.data?.message || 'Failed to fetch deleted members');
        setLoading(false);
      }
    }
  }, [page, rowsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    isMountedRef.current = true;
    abortControllerRef.current = new AbortController();
    fetchDeletedMembers();

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [fetchDeletedMembers]);

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    if (isMountedRef.current) {
      setNotification({
        show: true,
        message,
        type
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({...prev, show: false}));
  };

  const handleRestoreMember = async (id: string) => {
    if (!window.confirm('Are you sure you want to restore this member?')) {
      return;
    }

    try {
      await apiService.restoreMember(id);
      setMembers(prevMembers => prevMembers.filter(member => member._id !== id));
      setTotalCount(prev => Math.max(prev - 1, 0));
      showNotification('Member restored successfully', 'success');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to restore member';
      showNotification(message, 'error');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'suspended':
        return 'warning';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleRefresh = () => {
    fetchDeletedMembers();
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    shouldRefocusSearchRef.current = searchTerm.trim().length > 0;
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    shouldRefocusSearchRef.current = searchTerm.trim().length > 0;
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (!loading && shouldRefocusSearchRef.current && searchTerm.trim().length > 0) {
      if (document.activeElement !== searchInputRef.current) {
        searchInputRef.current?.focus();
      }
      shouldRefocusSearchRef.current = false;
    }
  }, [loading, searchTerm]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box />
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search deleted members..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          inputRef={searchInputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            onClick={handleRefresh}
            variant="contained"
            startIcon={<Refresh />}
          >
            Retry
          </Button>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Membership Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Deleted On</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box display="flex" alignItems="center" justifyContent="center" py={3}>
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 2 }}>Loading deleted members...</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {members.map((member) => (
              <TableRow key={member._id} hover>
                <TableCell>
                  <Typography variant="body1">
                    {member.fullName || `${member.firstName} ${member.lastName}`}
                  </Typography>
                </TableCell>
                <TableCell>
                  {typeof member.phone === 'string'
                    ? member.phone
                    : member.phone?.mobile || ''}
                </TableCell>
                <TableCell>
                  <Chip
                    label={member.membershipType}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={member.membershipStatus}
                    color={getStatusColor(member.membershipStatus) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {member.updatedAt ? new Date(member.updatedAt).toLocaleDateString() : 'N/A'}
                </TableCell>
                <TableCell>
                  <Tooltip title="Restore Member">
                    <IconButton
                      size="small"
                      onClick={() => handleRestoreMember(member._id)}
                      color="primary"
                    >
                      <RestoreFromTrash />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalCount > 0 && (
        <Paper sx={{ mt: 2 }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
            labelRowsPerPage="Members per page:"
          />
        </Paper>
      )}

      {members.length === 0 && debouncedSearchTerm && !loading && (
        <Box textAlign="center" mt={4}>
          <Typography color="textSecondary">
            No deleted members found matching "{debouncedSearchTerm}"
          </Typography>
        </Box>
      )}

      {members.length === 0 && !loading && !debouncedSearchTerm && totalCount === 0 && (
        <Box textAlign="center" mt={4}>
          <Typography color="textSecondary" gutterBottom>
            No deleted members found
          </Typography>
        </Box>
      )}

      <Snackbar
        open={notification.show}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.type} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeletedMembers;
